/**
 * Minimal WYSIWYG HTML editor built on contentEditable.
 *
 * The repo has no rich-text editor dependency (no tiptap/quill/slate/lexical),
 * and adding one for a prototype isn't worth the bundle, so this uses
 * contentEditable + document.execCommand. execCommand is formally deprecated
 * but remains implemented in every current browser and is by far the smallest
 * way to get bold/italic/lists/links working.
 *
 * Key detail: the DOM is NOT re-rendered from props while focused. Writing
 * innerHTML on every keystroke would reset the caret to the start of the
 * document, so incoming `value` is only applied when it diverges and the editor
 * does not have focus.
 */

import { useRef, useEffect, useCallback, useState } from "react"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { uploadImage, IMAGE_ACCEPT } from "../shared/uploadImage"
import { MediaLibraryButton } from "../shared/components/MediaLibrary"
import { cn } from "@/lib/utils"
import { sanitizeHtml } from "@/lib/sanitizeHtml"
import {
  Bold, Italic, Underline, List, ListOrdered, Link2, Heading1, Heading2, Heading3,
  ImagePlus, Loader2,
  Code2, Quote, Undo2, Redo2, Eraser, Eye, FileCode, Table,
  Image as ImageIcon, X,
} from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { ARTICLE_CONTENT_CLASS, ARTICLE_SNIPPETS } from "./articleStyles"

interface WysiwygEditorProps {
  value: string
  onChange: (html: string) => void
  className?: string
  placeholder?: string
  disabled?: boolean
}

type Mode = "rich" | "html" | "preview"

export function WysiwygEditor({ value, onChange, className, placeholder, disabled }: WysiwygEditorProps) {
  const ref = useRef<HTMLDivElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  /** The scroll/position container the drag handle is drawn against. */
  const paneRef = useRef<HTMLDivElement>(null)

  /**
   * The image the author last clicked, if any.
   *
   * Held as a DOM node rather than an index or selector: the body is
   * contentEditable, so any position-based reference goes stale the moment
   * text above it changes. Cleared when the click lands anywhere else.
   */
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>("rich")
  const [htmlDraft, setHtmlDraft] = useState(value)

  // Sync external value in, but never while the user is typing in it.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (document.activeElement === el) return
    const safe = sanitizeHtml(value)
    if (el.innerHTML !== safe) el.innerHTML = safe
  }, [value, mode])

  useEffect(() => { setHtmlDraft(value) }, [value])

  /**
   * Reads the body back out as HTML.
   *
   * The selection marker is stripped from the emitted copy: it is a UI affordance
   * and has no business in saved content.
   */
  const emit = useCallback(() => {
    const el = ref.current
    if (!el) return
    // Clone so the live DOM keeps its selection outline while the author works.
    const clean = el.cloneNode(true) as HTMLElement
    clean.querySelectorAll("img[data-selected]").forEach(n => n.removeAttribute("data-selected"))
    onChange(sanitizeHtml(clean.innerHTML))
  }, [onChange])

  const exec = useCallback((command: string, arg?: string) => {
    if (disabled) return
    ref.current?.focus()
    try {
      document.execCommand(command, false, arg)
    } catch {
      /* execCommand can throw in odd selection states; ignore and keep the editor usable */
    }
    emit()
  }, [disabled, emit])

  const addLink = useCallback(() => {
    const url = window.prompt("Link URL")
    if (url) exec("createLink", url)
  }, [exec])

  const block = useCallback((tag: string) => exec("formatBlock", tag), [exec])

  /**
   * Inserts a snippet at the caret. insertHTML is the only execCommand that
   * places arbitrary markup, and it keeps the browser's own undo stack intact —
   * setting innerHTML directly would break undo and move the caret.
   */
  const insertSnippet = useCallback((html: string) => {
    if (disabled) return
    ref.current?.focus()
    try {
      document.execCommand("insertHTML", false, html)
    } catch { /* ignore — leave the document untouched */ }
    emit()
  }, [disabled, emit])

  /**
   * Uploads an image and inserts it at the caret.
   *
   * Article bodies are HTML, so an image is an <img> in the markup rather than
   * a field on the record — which is why this lives here and not in
   * ImageField. The URL comes from the shared uploader, so the network
   * boundary and the validation are identical to the other backends.
   */
  // The caret, kept while the media library has focus, so a picked image lands where it was
  const savedRange = useRef<Range | null>(null)
  const rememberCaret = () => {
    const sel = window.getSelection()
    savedRange.current = sel && sel.rangeCount && ref.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null
  }
  const insertFromLibrary = (url: string) => {
    ref.current?.focus()
    const sel = window.getSelection()
    if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current) }
    insertSnippet(`<img src="${url.replace(/"/g, "&quot;")}" alt="" />`)
  }

  const insertImage = useCallback(async (file: File) => {
    setUploadError(null)
    setUploading(true)
    const result = await uploadImage(file)
    setUploading(false)
    if (imageInputRef.current) imageInputRef.current.value = ""

    if (!result.ok || !result.url) {
      setUploadError(result.error ?? "Upload failed.")
      return
    }
    // alt is intentionally empty: a caption the author has not written is
    // worse than none, and a wrong alt is worse than a missing one. The
    // author can add it in the HTML pane.
    const escaped = result.url.replace(/"/g, "&quot;")
    insertSnippet(`<img src="${escaped}" alt="" />`)
  }, [insertSnippet])

  /**
   * Opens links in article content in a new tab.
   *
   * Handles both panes through one delegated handler:
   * - Preview: a plain click opens the link. The default would navigate the
   *   whole SPA away and lose unsaved edits, so it's always intercepted.
   * - Rich edit: browsers don't follow links inside contentEditable at all, so
   *   ctrl/cmd-click is the opt-in — a bare click still places the caret for
   *   editing, which is what you want while writing.
   */
  /**
   * Sets the selected image's width, as a percentage of the column.
   *
   * A percentage rather than pixels: article bodies render at different
   * widths (the editor pane, the published page, a phone), and a fixed pixel
   * width that looks right in one is wrong in the others. `null` clears the
   * override and returns the image to its natural size.
   */
  /**
   * Where to draw the drag handle, in pane-relative pixels.
   *
   * Recomputed rather than stored on the image, because the handle floats in
   * an overlay: the image sits inside contentEditable content that scrolls
   * and reflows, and an absolutely-positioned handle has to follow it.
   */
  const [handleBox, setHandleBox] = useState<{ left: number; top: number; w: number; h: number } | null>(null)

  const measureHandle = useCallback(() => {
    const img = selectedImage
    const host = paneRef.current
    if (!img || !host || !img.isConnected) { setHandleBox(null); return }
    /*
     * Offsets are relative to the pane, which is the handle's positioning
     * parent -- so the handle travels with the pane when the PAGE scrolls,
     * and no scroll offset belongs in this maths. The pane itself never
     * scrolls (it grows instead; see the note on the editable div).
     */
    const i = img.getBoundingClientRect()
    const h = host.getBoundingClientRect()
    setHandleBox({ left: i.left - h.left, top: i.top - h.top, w: i.width, h: i.height })
  }, [selectedImage])

  /*
   * Keep the handle on the image.
   *
   * The ResizeObserver watches BOTH the image (it grows mid-drag) and the
   * pane (the column reflows when the editor is resized, which moves the
   * image without changing its size). A window resize listener covers the
   * rest. Page scrolling needs no listener: the handle is positioned inside
   * the pane, so it scrolls along with it.
   */
  useEffect(() => {
    if (!selectedImage) { setHandleBox(null); return }
    measureHandle()

    const ro = new ResizeObserver(measureHandle)
    ro.observe(selectedImage)
    if (paneRef.current) ro.observe(paneRef.current)
    window.addEventListener("resize", measureHandle)
    return () => {
      ro.disconnect()
      window.removeEventListener("resize", measureHandle)
    }
  }, [selectedImage, measureHandle])

  /**
   * Drag-to-resize from the bottom-right corner.
   *
   * Width is committed as a PERCENTAGE of the content box, matching the
   * preset buttons, so a dragged image behaves the same as a clicked one at
   * every render width. Height follows from h-auto in articleStyles, which
   * is why only the horizontal delta is used -- dragging vertically would
   * imply an aspect-ratio change the markup cannot express.
   */
  const startDrag = useCallback((e: React.PointerEvent) => {
    const img = selectedImage
    const host = paneRef.current
    if (!img || !host) return

    e.preventDefault()
    e.stopPropagation()
    // Pointer capture keeps events coming even when the cursor leaves the
    // handle, which it immediately does once the image starts growing.
    const handle = e.currentTarget as HTMLElement
    handle.setPointerCapture(e.pointerId)

    const startX = e.clientX
    const startW = img.getBoundingClientRect().width
    /*
     * The percentage basis is the content width of the EDITABLE div, not the
     * pane wrapper: the padding lives on the editable div, and a percentage
     * width resolves against the element's own content box. Measuring the
     * wrapper instead made a 50% drag commit as 48%, so the image jumped
     * on release.
     */
    const content = ref.current ?? host
    const cs = getComputedStyle(content)
    const basis = content.clientWidth
      - Number.parseFloat(cs.paddingLeft || "0")
      - Number.parseFloat(cs.paddingRight || "0")

    const onMove = (ev: PointerEvent) => {
      const next = startW + (ev.clientX - startX)
      // 5% floor so an image cannot be dragged to nothing and become
      // impossible to grab again.
      const pct = Math.max(5, Math.min(100, (next / basis) * 100))
      img.style.width = `${Math.round(pct)}%`
      img.removeAttribute("width")
      img.removeAttribute("height")
      measureHandle()
    }
    const onUp = () => {
      handle.releasePointerCapture(e.pointerId)
      handle.removeEventListener("pointermove", onMove)
      handle.removeEventListener("pointerup", onUp)
      // Committed once at the end: emitting per move would push a history
      // entry for every pixel of the drag.
      emit()
    }
    handle.addEventListener("pointermove", onMove)
    handle.addEventListener("pointerup", onUp)
  }, [selectedImage, emit, measureHandle])

  /*
   * Drop the selection when leaving the rich pane, or when the selected image
   * is no longer in the document -- the author may have deleted it, or the
   * body may have been replaced by a locale switch. Holding a detached node
   * would leave the resize strip pointing at nothing.
   */
  useEffect(() => {
    if (mode !== "rich") { setSelectedImage(null); return }
    if (selectedImage && !selectedImage.isConnected) setSelectedImage(null)
  }, [mode, selectedImage, value])

  /**
   * The selected image's current width preset, or null when unset.
   *
   * Read from the DOM rather than tracked in state: the author can also edit
   * the width by hand in the HTML pane, and state would then disagree with
   * the markup.
   */
  const currentWidth = (() => {
    const raw = selectedImage?.style.width
    if (!raw) return null
    const n = Number.parseFloat(raw)
    return raw.endsWith("%") && Number.isFinite(n) ? Math.round(n) : null
  })()

  const resizeSelected = useCallback((percent: number | null) => {
    const img = selectedImage
    if (!img) return
    if (percent === null) {
      img.style.removeProperty("width")
      // Also drop legacy width/height attributes, which would otherwise keep
      // overriding the stylesheet after the inline style is gone.
      img.removeAttribute("width")
      img.removeAttribute("height")
    } else {
      img.style.width = `${percent}%`
      img.removeAttribute("width")
      img.removeAttribute("height")
    }
    emit()
  }, [selectedImage, emit])

  const handleContentClick = useCallback((e: React.MouseEvent, pane: "rich" | "preview") => {
    // Clicking an image selects it for resizing; clicking elsewhere clears.
    const target = e.target as HTMLElement | null
    if (pane === "rich") {
      const img = target?.closest?.("img") as HTMLImageElement | null
      /*
       * The outline is applied via a data attribute so plain CSS can show it.
       * It is stripped from the previously-selected node first, and again on
       * emit(), so it never reaches the saved HTML.
       */
      const root = ref.current
      root?.querySelectorAll("img[data-selected]").forEach(el => el.removeAttribute("data-selected"))
      if (img) img.setAttribute("data-selected", "")
      setSelectedImage(img ?? null)
    }

    const anchor = target?.closest?.("a")
    if (!anchor) return
    const href = anchor.getAttribute("href")
    if (!href) return

    if (pane === "rich" && !(e.ctrlKey || e.metaKey)) return

    e.preventDefault()
    e.stopPropagation()

    // Only follow web links. Anything else (javascript:, data:, a bare fragment)
    // is not something to hand to a new tab.
    if (!/^https?:\/\//i.test(href)) return
    window.open(href, "_blank", "noopener,noreferrer")
  }, [])

  const Tool = ({ icon: Icon, label, onClick }: { icon: any; label: string; onClick: () => void }) => (
    <Button
      type="button" variant="ghost" size="icon"
      className="h-7 w-7" title={label} aria-label={label}
      onClick={onClick} disabled={disabled}
    >
      <Icon className="h-3.5 w-3.5" />
    </Button>
  )

  return (
    <div className={cn("flex flex-col rounded-md border bg-background", className)}>
      <input
        ref={imageInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0]
          if (f) insertImage(f)
        }}
      />
      {uploadError && (
        <p className="border-b bg-destructive/5 px-2 py-1 text-[11px] text-destructive">{uploadError}</p>
      )}

      {/*
        Image sizing, shown only while an image is selected. A separate strip
        rather than always-present toolbar buttons: the controls apply to one
        image, so offering them with nothing selected invites a click that
        silently does nothing.
      */}
      {mode === "rich" && selectedImage && (
        <div className="flex items-center gap-1 border-b bg-muted/40 px-2 py-1">
          <ImageIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="mr-1 text-[11px] text-muted-foreground">Width</span>
          {[25, 50, 75, 100].map(pct => (
            <Button
              key={pct}
              type="button"
              variant={currentWidth === pct ? "secondary" : "ghost"}
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => resizeSelected(pct)}
            >
              {pct}%
            </Button>
          ))}
          <Button
            type="button"
            variant={currentWidth === null ? "secondary" : "ghost"}
            size="sm"
            className="h-6 px-2 text-[11px]"
            title="Remove the width override and use the image's natural size"
            onClick={() => resizeSelected(null)}
          >
            Auto
          </Button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setSelectedImage(null)}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Deselect image"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-0.5 border-b p-1">
        {mode === "rich" && (
          <>
            <Tool icon={Bold} label="Bold" onClick={() => exec("bold")} />
            <Tool icon={Italic} label="Italic" onClick={() => exec("italic")} />
            <Tool icon={Underline} label="Underline" onClick={() => exec("underline")} />
            <Separator orientation="vertical" className="mx-1 h-5" />
            <Tool icon={Heading1} label="Heading 1" onClick={() => block("<h1>")} />
            <Tool icon={Heading2} label="Heading 2" onClick={() => block("<h2>")} />
            <Tool icon={Heading3} label="Heading 3" onClick={() => block("<h3>")} />
            <Tool icon={Quote} label="Quote" onClick={() => block("<blockquote>")} />
            <Tool icon={Code2} label="Code block" onClick={() => block("<pre>")} />
            <Separator orientation="vertical" className="mx-1 h-5" />
            <Tool icon={List} label="Bulleted list" onClick={() => exec("insertUnorderedList")} />
            <Tool icon={ListOrdered} label="Numbered list" onClick={() => exec("insertOrderedList")} />
            <Tool icon={Link2} label="Insert link" onClick={addLink} />
            <Tool
              icon={uploading ? Loader2 : ImagePlus}
              label={uploading ? "Uploading…" : "Insert image"}
              onClick={() => imageInputRef.current?.click()}
            />
            <MediaLibraryButton icon disabled={disabled} onOpen={rememberCaret} onPick={insertFromLibrary} />
            <Separator orientation="vertical" className="mx-1 h-5" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" title="Insert element" disabled={disabled}>
                  <Table className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {ARTICLE_SNIPPETS.map(sn => (
                  <DropdownMenuItem key={sn.label} onClick={() => insertSnippet(sn.html)} className="flex-col items-start gap-0">
                    <span className="text-xs font-medium">{sn.label}</span>
                    <span className="text-[10px] text-muted-foreground">{sn.description}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Separator orientation="vertical" className="mx-1 h-5" />
            <Tool icon={Undo2} label="Undo" onClick={() => exec("undo")} />
            <Tool icon={Redo2} label="Redo" onClick={() => exec("redo")} />
            <Tool icon={Eraser} label="Clear formatting" onClick={() => exec("removeFormat")} />
          </>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          <Button
            type="button" variant={mode === "rich" ? "secondary" : "ghost"} size="sm"
            className="h-7 px-2 text-xs" onClick={() => setMode("rich")}
          >
            Rich
          </Button>
          <Button
            type="button" variant={mode === "html" ? "secondary" : "ghost"} size="sm"
            className="h-7 gap-1 px-2 text-xs" onClick={() => setMode("html")}
          >
            <FileCode className="h-3 w-3" /> HTML
          </Button>
          <Button
            type="button" variant={mode === "preview" ? "secondary" : "ghost"} size="sm"
            className="h-7 gap-1 px-2 text-xs" onClick={() => setMode("preview")}
          >
            <Eye className="h-3 w-3" /> Preview
          </Button>
        </div>
      </div>

      {mode === "rich" && (
        <div ref={paneRef} className="relative">
        <div
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onClick={(e) => handleContentClick(e, "rich")}
          title="Ctrl/Cmd-click a link to open it in a new tab" 
          data-placeholder={placeholder}
          className={cn(
            ARTICLE_CONTENT_CLASS,
            // Outlines the image the author has selected for resizing.
            "[&_img[data-selected]]:outline [&_img[data-selected]]:outline-2 [&_img[data-selected]]:outline-primary",
            /*
             * Grows with its content instead of scrolling internally. A
             * nested overflow:auto here trapped the wheel over the body, so
             * a long article could not be scrolled past to reach the fields
             * below it -- the page's own ScrollArea does that job.
             */
            "p-3 outline-hidden min-h-[200px]",
            "empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]",
          )}
        />

        {/*
          Drag handle, drawn over the selected image's bottom-right corner.
          It lives in an overlay rather than inside the content because
          anything inserted into contentEditable becomes part of the article.
        */}
        {handleBox && !disabled && (
          <div
            onPointerDown={startDrag}
            title="Drag to resize"
            style={{
              left: handleBox.left + handleBox.w - 6,
              top: handleBox.top + handleBox.h - 6,
            }}
            className={cn(
              "absolute z-10 h-3 w-3 cursor-nwse-resize rounded-sm",
              "border-2 border-background bg-primary shadow-sm",
            )}
          />
        )}
        </div>
      )}

      {mode === "html" && (
        <textarea
          value={htmlDraft}
          disabled={disabled}
          onChange={(e) => setHtmlDraft(e.target.value)}
          onBlur={() => onChange(sanitizeHtml(htmlDraft))}
          spellCheck={false}
          // resize-y (not none): the source view is the one place a manual
          // height is genuinely useful, and it cannot auto-grow like a div.
          className="min-h-[320px] w-full resize-y bg-transparent p-3 font-mono text-xs outline-hidden"
        />
      )}

      {mode === "preview" && (
        <div
          className={cn(ARTICLE_CONTENT_CLASS, "min-h-[200px] p-3")}
          onClick={(e) => handleContentClick(e, "preview")}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) || "<p class='text-muted-foreground'>Nothing to preview.</p>" }}
        />
      )}
    </div>
  )
}
